import React, { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
    LayoutDashboard,
    Users2,
    Briefcase,
    CalendarDays,
    ClipboardList,
    FileText,
    FolderKanban,
    BellRing,
    IndianRupee,
    BarChart3,
    ScrollText,
    Settings2,
    LogOut,
    Search,
    ChevronDown,
    Building2,
    UserCircle,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import api from "@/api/client";

const NAV = [
    { to: "/platform", label: "Firms", icon: Building2, roles: ["super_admin"] },
    { to: "/platform/analytics", label: "Platform Analytics", icon: BarChart3, roles: ["super_admin"] },
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, roles: ["admin", "employee"] },
    { to: "/clients", label: "Clients", icon: Briefcase, roles: ["admin", "employee"] },
    { to: "/employees", label: "Employees", icon: Users2, roles: ["admin"] },
    { to: "/tasks", label: "Tasks", icon: ClipboardList, roles: ["admin"] },
    { to: "/tasks", label: "My Tasks", icon: ClipboardList, roles: ["employee"] },
    { to: "/compliance", label: "Compliance", icon: FolderKanban, roles: ["admin", "employee"] },
    { to: "/documents", label: "Documents", icon: FileText, roles: ["admin", "employee"] },
    { to: "/calendar", label: "Calendar", icon: CalendarDays, roles: ["admin", "employee"] },
    { to: "/payments", label: "Payments", icon: IndianRupee, roles: ["admin"] },
    { to: "/reports", label: "Reports", icon: BarChart3, roles: ["admin"] },
    { to: "/audit", label: "Audit Logs", icon: ScrollText, roles: ["admin"] },
    { to: "/settings", label: "Settings", icon: Settings2, roles: ["admin"] },
    { to: "/profile", label: "My Profile", icon: UserCircle, roles: ["employee"] },
];

function initials(name = "") {
    return name
        .split(" ")
        .map((s) => s[0])
        .filter(Boolean)
        .slice(0, 2)
        .join("")
        .toUpperCase();
}

export default function AppLayout() {
    const { user, logout } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const [unread, setUnread] = useState(0);
    const [firm, setFirm] = useState(null);

    useEffect(() => {
        api
            .get("/notifications")
            .then((r) => setUnread(r.data.unread || 0))
            .catch(() => {});
        if (user?.firm_id) {
            api
                .get("/settings/firm")
                .then((r) => setFirm(r.data))
                .catch(() => {});
        }
    }, [location.pathname, user?.firm_id]);

    const items = NAV.filter((n) => n.roles.includes(user?.role));

    return (
        <div className="min-h-screen flex bg-slate-50">
            <aside
                className="w-64 shrink-0 border-r border-slate-200 bg-white flex flex-col sticky top-0 h-screen"
                data-testid="app-sidebar"
            >
                <div className="h-16 flex items-center gap-2 px-5 border-b border-slate-200">
                    <div className="w-8 h-8 rounded-lg bg-primary text-white grid place-items-center font-display font-bold">
                        C
                    </div>
                    <div>
                        <div className="font-display font-bold text-[15px] text-slate-900 leading-none">
                            CAFlow
                        </div>
                        <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-0.5">
                            Practice OS
                        </div>
                    </div>
                </div>
                <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-0.5">
                    {items.map((it) => (
                        <NavLink
                            key={it.to}
                            to={it.to}
                            className={({ isActive }) =>
                                `side-link ${isActive ? "active" : ""}`
                            }
                            data-testid={`nav-${it.to.replace("/", "")}`}
                        >
                            <it.icon size={17} strokeWidth={1.75} />
                            <span>{it.label}</span>
                            {it.to === "/notifications" && unread > 0 ? (
                                <Badge className="ml-auto bg-primary text-white">{unread}</Badge>
                            ) : null}
                        </NavLink>
                    ))}
                </nav>
                <div className="p-3 border-t border-slate-200 text-[11px] text-slate-500">
                    <div className="flex items-center gap-2">
                        <Building2 size={13} />
                        <span className="truncate">{firm?.name || "—"}</span>
                    </div>
                    <div className="mt-1">Plan: {firm?.plan || "trial"}</div>
                </div>
            </aside>

            <div className="flex-1 flex flex-col min-w-0">
                <header
                    className="h-16 sticky top-0 z-30 border-b border-slate-200 bg-white/85 backdrop-blur-md flex items-center px-6 gap-4"
                    data-testid="app-header"
                >
                    <div className="hidden md:flex items-center gap-2 text-slate-400 max-w-md w-full">
                        <Search size={16} />
                        <input
                            type="text"
                            placeholder="Search clients, tasks…"
                            className="bg-transparent outline-none text-sm text-slate-700 placeholder:text-slate-400 flex-1"
                            data-testid="global-search"
                            onKeyDown={(e) => {
                                if (e.key === "Enter" && e.currentTarget.value.trim()) {
                                    navigate(`/clients?q=${encodeURIComponent(e.currentTarget.value.trim())}`);
                                }
                            }}
                        />
                    </div>
                    <div className="ml-auto flex items-center gap-3">
                        <Button
                            variant="ghost"
                            size="icon"
                            className="relative"
                            onClick={() => navigate("/notifications")}
                            data-testid="header-notifications"
                            aria-label="Notifications"
                        >
                            <BellRing size={18} />
                            {unread > 0 && (
                                <span className="absolute top-1 right-1.5 w-2 h-2 rounded-full bg-rose-500" />
                            )}
                        </Button>
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <button
                                    className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-full hover:bg-slate-50 transition-colors"
                                    data-testid="header-user-menu"
                                >
                                    <Avatar className="h-8 w-8">
                                        <AvatarFallback className="bg-primary text-white text-xs font-semibold">
                                            {initials(user?.name)}
                                        </AvatarFallback>
                                    </Avatar>
                                    <div className="text-left leading-tight hidden sm:block">
                                        <div className="text-sm font-medium text-slate-800">
                                            {user?.name}
                                        </div>
                                        <div className="text-[11px] text-slate-500 uppercase tracking-wider">
                                            {user?.role?.replace("_", " ")}
                                        </div>
                                    </div>
                                    <ChevronDown size={14} className="text-slate-400" />
                                </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-56">
                                <DropdownMenuLabel>{user?.email}</DropdownMenuLabel>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="menu-settings">
                                    <Settings2 size={14} className="mr-2" /> Settings
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                    onClick={async () => {
                                        await logout();
                                        navigate("/login");
                                    }}
                                    data-testid="menu-logout"
                                >
                                    <LogOut size={14} className="mr-2" /> Log out
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </header>
                <main className="flex-1 p-6 lg:p-8">
                    <Outlet />
                </main>
            </div>
        </div>
    );
}
